import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PieChart, Pie, Cell, Sector, ResponsiveContainer, Legend } from 'recharts';
import GraphLoader from '../common/GraphLoader';
import config from '../../config';

// ----------------------------------------------------
// Recharts Custom Renderers
// ----------------------------------------------------
const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;

    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 8}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                className="transition-all duration-300 ease-in-out"
            />
            <text x={cx} y={cy + outerRadius + 30} textAnchor="middle" fill={fill} className="text-xs font-semibold uppercase tracking-widest">
                {`${payload.name}: ${payload.value.toLocaleString()}`}
            </text>
        </g>
    );
};

const CustomLegend = ({ payload, onLegendClick, hiddenSectors }) => {
    return (
        <div className="w-full mt-2 flex flex-col items-center">
            <ul className="flex flex-wrap justify-center text-[10px] uppercase tracking-widest font-semibold text-black gap-y-2">
                {payload.map((entry, index) => {
                    const isHidden = hiddenSectors.includes(entry.value);
                    const percentage = entry.payload.percentage;

                    return (
                        <li
                            key={`item-${index}`}
                            className={`flex items-center mx-3 cursor-pointer transition-all duration-300 ${isHidden ? 'opacity-40 grayscale' : 'opacity-100 hover:scale-105'}`}
                            onClick={() => onLegendClick(entry.value)}
                        >
                            <span className="w-2 h-2 mr-2 rounded-none transition-colors" style={{ backgroundColor: entry.color }}></span>
                            <span>{entry.value} {percentage !== undefined ? `(${percentage.toFixed(0)}%)` : ''}</span>
                        </li>
                    )
                })}
            </ul>
        </div>
    );
};

// ----------------------------------------------------
// Custom Polar Bar Chart (Nightingale Rose)
// ----------------------------------------------------
const NightingaleRoseChart = ({ data, totalValue, colors }) => {
    const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, data: null });

    if (!data || data.length === 0) return null;

    // Viewport and sizing math
    const size = 400;
    const center = size / 2;
    const maxRadius = center - 50; // Leave room for outward text labels
    const maxCount = Math.max(...data.map(d => d.count));
    const angleStep = (2 * Math.PI) / data.length;

    // Background Grid Intervals
    const gridFractions = [0.33, 0.66, 1];

    return (
        <div 
            className="relative w-full h-80 flex items-center justify-center animate-fade-in"
            onMouseLeave={() => setTooltip({ show: false, x: 0, y: 0, data: null })}
        >
            <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full max-h-[350px] drop-shadow-sm overflow-visible">
                
                {/* 1. Concentric Grid Circles */}
                {gridFractions.map(frac => (
                    <circle key={`grid-${frac}`} cx={center} cy={center} r={maxRadius * frac} fill="none" stroke="#E5E5E5" strokeWidth="1" />
                ))}

                {/* 2. Concentric Axis Value Labels */}
                {gridFractions.map(frac => {
                    const val = Math.round(maxCount * frac);
                    const yPos = center - (maxRadius * frac);
                    return (
                        <text key={`label-${frac}`} x={center + 4} y={yPos - 4} fontSize="9" fill="#A3A3A3" className="select-none font-medium tracking-wider">
                            {val}
                        </text>
                    );
                })}

                {/* 3. Radial Grid Lines and Category Labels */}
                {data.map((item, i) => {
                    const angle = i * angleStep - Math.PI / 2; // Start from top
                    const midAngle = angle + angleStep / 2;
                    
                    const x1 = center + maxRadius * Math.cos(angle);
                    const y1 = center + maxRadius * Math.sin(angle);
                    
                    const labelX = center + (maxRadius + 20) * Math.cos(midAngle);
                    const labelY = center + (maxRadius + 20) * Math.sin(midAngle);

                    return (
                        <g key={`axis-${i}`}>
                            <line x1={center} y1={center} x2={x1} y2={y1} stroke="#E5E5E5" strokeWidth="1" />
                            <text
                                x={labelX}
                                y={labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="10"
                                fill="#767676"
                                className="font-semibold uppercase tracking-widest select-none"
                            >
                                {item.reason_code}
                            </text>
                        </g>
                    );
                })}

                {/* 4. The Polar Data Slices */}
                {data.map((item, i) => {
                    const startAngle = i * angleStep - Math.PI / 2;
                    const endAngle = (i + 1) * angleStep - Math.PI / 2;
                    const radius = (item.count / maxCount) * maxRadius;

                    const x1 = center + radius * Math.cos(startAngle);
                    const y1 = center + radius * Math.sin(startAngle);
                    const x2 = center + radius * Math.cos(endAngle);
                    const y2 = center + radius * Math.sin(endAngle);

                    // Draw a wedge starting from center
                    const d = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`;
                    const percentage = ((item.count / totalValue) * 100).toFixed(1);

                    return (
                        <path
                            key={`slice-${i}`}
                            d={d}
                            fill={colors[i % colors.length]}
                            stroke="#ffffff"
                            strokeWidth="1.5"
                            className="transition-all duration-300 hover:opacity-85 hover:brightness-110 cursor-pointer origin-center"
                            onMouseMove={(e) => {
                                const rect = e.currentTarget.parentElement.parentElement.getBoundingClientRect();
                                setTooltip({
                                    show: true,
                                    x: e.clientX - rect.left,
                                    y: e.clientY - rect.top,
                                    data: { ...item, percentage }
                                });
                            }}
                        />
                    );
                })}
            </svg>

            {/* Custom Interactive Tooltip */}
            {tooltip.show && tooltip.data && (
                <div
                    className="absolute pointer-events-none bg-[#222] text-white p-3 rounded shadow-lg border border-[#444] z-50 transition-opacity duration-150"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translate(-50%, -120%)',
                    }}
                >
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-1 text-gray-300">
                        Reason: <span className="text-white">{tooltip.data.reason_code}</span>
                    </p>
                    <div className="w-full h-[1px] bg-gray-600 mb-2"></div>
                    <p className="text-xs font-semibold tracking-wider">
                        Count: <span className="text-[#c63939]">{tooltip.data.count.toLocaleString()}</span>
                    </p>
                    <p className="text-xs font-semibold tracking-wider mt-1">
                        Share: <span className="text-[#c63939]">{tooltip.data.percentage}%</span>
                    </p>
                </div>
            )}
        </div>
    );
};

// ----------------------------------------------------
// Main Component
// ----------------------------------------------------
// Application Red Theme
const REJECTED_COLORS = [
    '#3b1010', '#4a1515', '#5a1919', '#691e1e', '#792222', 
    '#882727', '#982b2b', '#a73030', '#b63434', '#c63939'
].reverse(); 

const InteractiveDonutChart = ({ year }) => {
    const [chartData, setChartData] = useState([]);
    const [rejectedData, setRejectedData] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [hiddenSectors, setHiddenSectors] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const isCurrentYear = year == new Date().getFullYear();

    useEffect(() => {
        if (!year) return;

        const fetchData = async () => {
            try {
                setChartData([]);
                setRejectedData([]);
                setHiddenSectors([]);
                setActiveIndex(null);
                setError(null);
                setIsLoading(true);

                const [statusRes, rejectedRes] = await Promise.all([
                    fetch(`${config.API_BASE_URL}/claim-status-distribution/`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ year }),
                    }),
                    fetch(`${config.API_BASE_URL}/rejected-claims-by-reason/?year=${year}`, {
                        method: "GET",
                        headers: { "Content-Type": "application/json" }
                    })
                ]);

                setIsLoading(false);

                if (!statusRes.ok) {
                    const errorData = await statusRes.json();
                    throw new Error(errorData.detail || "Failed to load status distribution");
                }

                const statusJson = await statusRes.json();
                setChartData(statusJson.data);

                if (rejectedRes.ok) {
                    const rejectedJson = await rejectedRes.json();
                    if (rejectedJson.success) {
                        // Sort descending so the largest bars anchor the start of the chart visually
                        const sortedRejections = rejectedJson.data.sort((a, b) => b.count - a.count);
                        setRejectedData(sortedRejections);
                    }
                }

            } catch (err) {
                console.error("Error fetching claims data:", err.message);
                setError(err.message);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [year]);

    const totalValue = useMemo(() => chartData.reduce((sum, entry) => sum + entry.value, 0), [chartData]);
    const totalRejectedValue = useMemo(() => rejectedData.reduce((sum, item) => sum + item.count, 0), [rejectedData]);

    const filteredChartData = useMemo(() => {
        const colorMap = {
            'Approved': '#000000',
            'Accepted': '#000000',
            'Rejected': '#8B0000',
            'Pending': '#CCCCCC',
            'Total': '#333333'
        };
        const fallbackColors = ['#000000', '#333333', '#CCCCCC', '#8B0000', '#767676'];

        const dataWithTotal = chartData.map((d, i) => ({
            ...d,
            total: totalValue,
            year,
            color: colorMap[d.name] || fallbackColors[i % fallbackColors.length]
        }));
        return dataWithTotal.filter(d => !hiddenSectors.includes(d.name));
    }, [chartData, hiddenSectors, totalValue, year]);

    const handleLegendClick = useCallback((name) => {
        setHiddenSectors(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);
    }, []);

    if (error) {
        return <div className="text-[#8B0000] text-xs uppercase tracking-widest p-6 text-center">Error: {error}</div>;
    }

    return (
        <div className="bg-white rounded-none border border-[#E5E5E5] p-6 w-full max-w-2xl mx-auto flex flex-col items-center shadow-sm">
            
            {/* Header Section */}
            <div className="w-full flex justify-between items-end mb-6">
                <div>
                    <h2 className="text-sm font-light text-black uppercase tracking-widest self-start mb-1">
                    Claims Status for {year}
                </h2>
                <h4 className="text-xs font-semibold text-[#767676] uppercase tracking-widest self-start">
                    {isCurrentYear ? 'Total claims till today' : 'Total'}: {totalValue.toLocaleString()}
                </h4>
                </div>
            </div>

            {isLoading ? (
                <div className="py-20 flex-1 flex items-center justify-center min-h-[500px]"><GraphLoader /></div>
            ) : (
                <div className="w-full flex flex-col gap-y-10">
                    
                    {/* Top Chart: Recharts Donut */}
                    <div className="w-full h-72 relative flex flex-col items-center justify-center shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                                <Pie
                                    activeIndex={activeIndex}
                                    activeShape={renderActiveShape}
                                    data={filteredChartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="50%"
                                    outerRadius="65%"
                                    paddingAngle={3}
                                    dataKey="value"
                                    onMouseEnter={(_, index) => setActiveIndex(index)}
                                    onMouseLeave={() => setActiveIndex(null)}
                                    stroke="none"
                                    isAnimationActive={true}
                                >
                                    {filteredChartData.map((entry) => (
                                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Legend
                                    content={<CustomLegend onLegendClick={handleLegendClick} hiddenSectors={hiddenSectors} />}
                                    verticalAlign="bottom"
                                    wrapperStyle={{ paddingTop: "20px" }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Bottom Chart: Custom SVG Polar Bar (Nightingale) */}
                    {rejectedData.length > 0 && !hiddenSectors.includes('Rejected') && (
                        <div className="w-full pt-8 border-t border-[#E5E5E5] flex flex-col items-center">
                            <h4 className="text-[11px] font-semibold text-[#767676] uppercase tracking-widest self-start mb-6">
                                Rejected Reasons Breakdown
                            </h4>
                            
                            <NightingaleRoseChart 
                                data={rejectedData} 
                                totalValue={totalRejectedValue} 
                                colors={REJECTED_COLORS} 
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InteractiveDonutChart;